import { AllEntities } from "@/components/record/types/record.entities";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { v4 } from "uuid";
interface IGlobalState {
  sessionId: string;
  isEditorOpen: boolean;
  document: string;
  recordRevision: number;
  drawers: Array<EntityState>;
  reloadActivities: number;
}

const initialState = {
  sessionId: v4(),
  isEditorOpen: false,
  recordRevision: 0,
  drawers: [],
  document: "",
  reloadActivities: 0,
} satisfies IGlobalState as IGlobalState;

interface EntityState {
  entity: string;
  entityId?: string;
  entityIndex?: string;
}

export const globalSlice = createSlice({
  name: "global",
  initialState,
  reducers: {
    changeRecordRevision: (state, action: PayloadAction<number>) => {
      state.recordRevision = action.payload;
    },
    toggleEditor: (state) => {
      state.isEditorOpen = !state.isEditorOpen;
    },
    setDocument: (state, action: PayloadAction<{ document?: string }>) => {
      state.document = action.payload.document ?? "";
    },
    openDrawer: (state, action: PayloadAction<EntityState>) => {
      state.drawers.push({
        entity: action.payload.entity,
        entityId: action.payload.entityId,
        entityIndex: action.payload.entityIndex,
      });
    },
    closeDrawer: (state, action: PayloadAction<EntityState>) => {
      state.drawers = state.drawers.filter(
        (drawer) =>
          !(
            drawer.entity === action.payload.entity &&
            (drawer.entityId === action.payload.entityId ||
              drawer.entityIndex === action.payload.entityIndex)
          )
      );
      state.document = "";
    },
    setDrawersFromUrl: (state, action: PayloadAction<EntityState[]>) => {
      state.drawers = action.payload;
    },
    setReloadActivities: (state, action: PayloadAction<number>) => {
      state.reloadActivities = action.payload;
    },
  },
});

// Async action to open a drawer and update the URL in the same dispatch
export const openDrawerAndSyncUrl =
  ({
    entity,
    entityId,
    entityIndex,
    extraQueryParams,
  }: {
    entity: AllEntities;
    entityId?: any;
    entityIndex?: string;
    extraQueryParams?: string;
  }) =>
  (dispatch: any, getState: any) => {
    // First dispatch the action to update the state
    dispatch(openDrawer({ entity, entityId, entityIndex }));

    // Then, update the URL to reflect the change

    const drawers = getState().global.drawers;
    const queryParams = drawers
      .map(
        (d: EntityState) => `drawer=${d.entity}:${d.entityId ?? d.entityIndex}`
      )
      .join("&");

    window.history.pushState(
      null,
      "",
      `?${queryParams}${extraQueryParams ? `&${extraQueryParams}` : ""}`
    );
  };

export const closeDrawerAndSyncUrl =
  ({
    entity,
    entityId,
    entityIndex,
  }: {
    entity: AllEntities;
    entityId?: any;
    entityIndex?: string;
  }) =>
  (dispatch: any, getState: any) => {
    // First dispatch the action to update the state
    dispatch(closeDrawer({ entity, entityId, entityIndex }));

    // Then, update the URL to reflect the change
    const drawers = getState().global.drawers;

    if (drawers.length === 0) {
      window.history.pushState(null, "", window.location.pathname);
      return;
    }

    const queryParams = drawers
      .map(
        (d: EntityState) => `drawer=${d.entity}:${d.entityId ?? d.entityIndex}`
      )
      .join("&");

    window.history.pushState(null, "", `?${queryParams}`);
  };

export const {
  toggleEditor,
  openDrawer,
  closeDrawer,
  setDrawersFromUrl,
  setDocument,
  changeRecordRevision,
  setReloadActivities,
} = globalSlice.actions;
export type GlobalState = ReturnType<typeof globalSlice.reducer>;
export default globalSlice.reducer;
